import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import OutputText from '@ey/canvascoreservices/OutputText';
import Input from '../../CoreWrappers/Input';
import Button from '../../CoreWrappers/Button';
import {UserLookupType, UserType} from '../../../util/uiconstants';
import {labels, currentResource} from '../../../util/utils';
import AddProjectOwner from './Common/AddProjectOwner';
import {IconoirPlusCircle, IconoirXmarkCircle} from '@ey-xd/motif-icon';
import AddNewDomain from './Common/AddNewDomain';
import AddExistingDomain from './Common/AddExisitingDomain';
import _ from 'lodash';
import styled from 'styled-components';
import {raiseJSValidationError, clearErrors} from '../../../actions/erroractions';
import ErrorSummary from '../../Common/ErrorSummary/ErrorSummary';
import {setIsModalOpen} from '../../../actions/AccessGroups/SetIsModalOpen';
import Modal from '../../CoreWrappers/Modal';

const EntityModal = (props) => {
	const dispatch = useDispatch();
	const {showModal, setShowModal, selectedNode, saveEntityDetails, existingDomains} = props;

	// state for entity name
	const [entityName, setEntityName] = useState(
		selectedNode.entityName == labels.enterEntityName ? null : selectedNode.entityName
	);
	const [entityNameError, setEntityNameError] = useState(false);

	// State for users
	const [userCount, setUserCount] = useState(0);
	const [userList, setUserList] = useState([]); // Contains newly added empty user object also
	const [users, setUsers] = useState([]); // conatins only valid selected user
	const [userError, setUserError] = useState(false);

	// State for contacts
	const [nodeContacts, setNodeContacts] = useState([]);

	// State for domains
	const [domainCount, setDomainCount] = useState(0);
	const [domainList, setDomainList] = useState([]); // Contains newly added empty domain object alse
	const [domains, setDomains] = useState([]); // conatins only valid domain
	const [domainError, setDomainError] = useState(false);
	const [checkDomainError, setCheckDomainError] = useState(false);

	// Populate existing users and domain of entity
	useEffect(() => {
		dispatch(clearErrors());
		if(_.size(selectedNode?.contacts) > 0){
			setNodeContacts(selectedNode?.contacts);
		}
		if (_.size(selectedNode?.admins) > 0) {
			let userList = [];
			const sortedUsers = _.sortBy(selectedNode?.admins, 'emailAddress');
			_.forEach(sortedUsers, (user) => {
				userList.push({
					id: user.userId,
					email: user.emailAddress,
					emailAddress: user.emailAddress,
					userId: user.userId,
					firstName: user.firstName,
					lastName: user.lastName,
					isInternal: user.userType == UserType.Member,
					isEmpty: false,
					roles: []
				});
			});
			setUsers(userList);
			setUserList(userList);
			setUserCount(userList.length);
			
		}
		if (_.size(selectedNode?.domains) > 0) {
			const sortedDomains = [...selectedNode?.domains].sort();
			let existingDomains = [];
			let existingDomainList = [];
			let counter = 0;
			_.forEach(sortedDomains, (domain) => {
				counter = counter + 1;
				existingDomainList.push({
					id: `domain_${counter}`,
					isEmpty: false,
					isDuplicate: false,
					isValidDomain: true,
					button: `${1}`,
					isOld: true
				});
				existingDomains.push({id: `domain_${counter}`, domainName: domain, isEmpty: false, isOld: true});
			});
			setDomainList(existingDomainList);
			setDomains(existingDomains);
			setDomainCount(existingDomainList.length);
		}
		return () => {
			resetStates();
		};
	}, [selectedNode]);

	// Run validation if any domain is removed
	// As setState is async process, we are doing validation using useeffect
	useEffect(() => {
		if (checkDomainError) {
			setCheckDomainError(false);
			if(domainValidator()) {
				setDomainError(false)
			}
		}
	}, [checkDomainError]);

	const resetStates = () => {
		setEntityName('');
		setEntityNameError(false);
		setUserList([]);
		setUsers([]);
		setUserError(false);
		setDomainList([]);
		setDomains([]);
		setDomainError(false);
		dispatch(clearErrors()).then(() => {
			dispatch(setIsModalOpen(false));
		});
	};

	// ** Entity owner functions - Start**
	// Delete user row
	const deleteEntityOwner = (item) => {
		setUserList(_.filter(userList, (user) => user.id != item.id));
		setUsers(_.filter(users, (user) => user.id != item.id));
		if (item.isNewAdd) {
			setUserError(false);
		}
	};

	// Add user row
	const addEntityOwnerbuttonClick = () => {
		const count = userCount + 1;
		setUserList([...userList, {id: `user_${count}`, isNewAdd: true}]);
		setUserCount(count);
	};

	// Once user is added, sync user and userList
	const setSelectedMember = (user) => {
		const existingList = _.filter(userList, (item) => !item.isNewAdd);
		setUserList([...existingList, user]);
	};

	// ** Entity owner functions - End **

	// ** Entity domain functions - Start **
	// Add domain row
	const addDomainbuttonClick = (val) => {
		const count = domainCount + 1;
		setDomainList([
			...domainList,
			{id: `domain_${count}`, isEmpty: true, isValidDomain: true, isDuplicate: false, button: `${val}`}
		]);
		setDomainError(false);
		setDomainCount(count);
	};

	// Update the domains data for the given id and fields
	const updateDomainObject = (id, domainFields, domainItems) => {
		let newDomains = [...domainItems];
		let index = domainItems.findIndex((el) => el.id === id);
		let domain = index >= 0 ? newDomains[index] : {id: id};
		Object.entries(domainFields).forEach(([key, value], index) => (domain[key] = value));
		newDomains[index >= 0 ? index : domainItems.length] = domain;
		return newDomains;
	};

	// Remove domain row
	const removeDomainRow = (key) => {
		let remainingItems = domainList.filter((el) => el.id !== key);

		// If removed item was duplicate, mark other duplicate domain as valid domain
		let removedItem = domainList.find((el) => el.id == key);
		if (removedItem.isDuplicate) {
			const duplicateDomainName = domains.find((el) => el.id == key)?.domainName;
			let otherDuplicateItem = domains.find((el) => el.id !== key && el.domainName == duplicateDomainName);
			const updatedDomains = updateDomainObject(
				otherDuplicateItem.id,
				{isValidDomain: true, isDuplicate: false},
				remainingItems
			);
			setDomainList(updatedDomains);
			setDomains(domains.filter((el) => el.id !== key));
			setCheckDomainError(true);
		} else {
			setDomains(domains.filter((el) => el.id !== key));
			setDomainList(domainList.filter((el) => el.id !== key));
			setCheckDomainError(true);
		}
	};

	// Update domain name. This will be called only for new domain being added
	const updateDomainName = (id, value) => {
		const updatedDomains = updateDomainObject(id, {domainName: value}, domains);
		setDomains(updatedDomains);
	};

	// Update domain name. This will be called if existing domain is being added. Here domain name will be always valid.
	const updateExitsingDomainName = (id, value) => {
		const updatedDomains = updateDomainObject(id, {domainName: value, isEmpty: false}, domains);
		setDomains(updatedDomains);

		const updatedDomainList = updateDomainObject(id, {isValidDomain: true, isEmpty: false}, domainList);
		setDomainList(updatedDomainList);
	};

	// Set flag if domain name is valid or invalid
	const updateDomainValidation = (id, isValidDomain) => {
		const updatedDomainList = updateDomainObject(id, {isValidDomain: isValidDomain}, domainList);
		setDomainList(updatedDomainList);
	};

	// Set isEmpty property for domain
	const setEmptyDomain = (id, value) => {
		const updatedDomainList = updateDomainObject(id, {isEmpty: value}, domainList);
		setDomainList(updatedDomainList);

		const updatedDomains = updateDomainObject(id, {isEmpty: value}, domains);
		setDomains(updatedDomains);

		if (domainError && !value) {
			setDomainError(false);
		}
	};

	// If newly added domain is duplicate
	const setDomainDuplicate = (id, value) => {
		const updatedDomainList = updateDomainObject(id, {isDuplicate: value}, domainList);
		setDomainList(updatedDomainList);
		if (value) {
			setDomainError(true);
		}
	};

	// Before adding any new domain, ensure existing domains are valid
	const domainValidator = () => {
		let isValidData = true;
		if (domainList.length > 0) {
			// If user clicked on add domain but has not provided valid domain name, set error flag to true
			if (domains.length < domainList.length) {
				setDomainError(true);
				isValidData = false;
			} else {
				setDomainError(false);
			}

			// If user provided duplicate name for any of the domains, set error flag to true
			if (domainList?.find((x) => x.isDuplicate)) {
				setDomainError(true);
				isValidData = false;
			} else {
				setDomainError(false);
			}

			// If any empty domain is added or domain name is not valid, set error flag to true
			const inValidDomains = domainList.some((el) => el.isEmpty === true || !el.isValidDomain);
			const emptyRow = domains.some((el) => el.isEmpty == true);
			if (inValidDomains || emptyRow) {
				setDomainError(true);
				isValidData = false;
			} else {
				setDomainError(false);
			}
		}

		return isValidData;
	};

	// ** Entity domain functions - End **

	// For each user, ensure domain name is already added. If no, show validation message
	const isInvalidUserDomain = () => {
		const allDomainsOfSelectedUsers = users.map((user) => `@${user.emailAddress.split('@')[1].toLowerCase()}`);
		
		// User domains like @*ey.com, @*.ey.com, @ey.com does not need validation
		const eyDomainExpression = new RegExp(/.*ey.com$/);
		const userDomainsToValidate = [];
		_.forEach(allDomainsOfSelectedUsers, (user) => {
			if (!eyDomainExpression.test(user)) {
				userDomainsToValidate.push(user);
			}
		});
		
		const availableDomains = domains.map((d) => d.domainName.toLowerCase());
		let isInValidUser = false;

		let nodeExternalContactsDomains = [];

		// Check on entity contacts if there's an external domain.
		nodeContacts?.forEach((contact) => {
			if (contact.contactId?.includes('@')){
				nodeExternalContactsDomains.push(`@${contact.contactId.split('@')[1].toLowerCase()}`);
			}
		});
		
		// Check entity contact list for external domains.
		if (nodeExternalContactsDomains.length > 0) {
			const uniqExternalContactsDomain = [...new Set(nodeExternalContactsDomains)]
			
			uniqExternalContactsDomain.forEach((domain) => {
				const found = availableDomains.find(domainName => domainName == domain);
				if (!found) {
					isInValidUser = true;
				}
			});

			// If there was at least one external contact on entity contacts, return and raise toast error.
			if (isInValidUser)
			{
				return isInValidUser;
			}
		}
			
		isInValidUser = userDomainsToValidate.some((d) => !_.includes(availableDomains, d));
		return isInValidUser;
	};

	// Modal validation on save changes
	const isValidData = () => {
		let isValidData = true;
		if (!entityName || _.trim(entityName).length <= 0) {
			isValidData = false;
			setEntityNameError(true);
		}
		if (userList.length > users.length) {
			isValidData = false;
			setUserError(true);
		}
		if (!domainValidator()) {
			isValidData = false;
			setDomainError(true);
		}
		if (entityNameError || userError || domainError) {
			isValidData = false;
		}
		if (isInvalidUserDomain()) {
			isValidData = false;
			dispatch(setIsModalOpen(true)).then(() => {
				dispatch(raiseJSValidationError(currentResource.Errors[378]));
			});
		}
		return isValidData;
	};
	// Save changes for the selected entity
	const setEntityDetails = () => {
		if (isValidData()) {
			let selectedUsers = [];
			let selectedDomains = [];
			_.forEach(users, (user) => {
				selectedUsers.push({
					userId: user.userId,
					firstName: user.firstName,
					lastName: user.lastName,
					emailAddress: user.emailAddress,
					userType: user.isInternal || user.userType == UserType.Member ? UserType.Member : UserType.Guest
				});
			});
			_.forEach(domains, (domain) => {
				selectedDomains.push(domain.domainName);
			});
			saveEntityDetails(selectedNode.id, _.trim(entityName), selectedUsers, selectedDomains);
			setShowModal(false);
		}
	};

	const renderAdminBtn = () => {
		const props = {
			svg: IconoirPlusCircle,
			iconSize: 20,
			variant: 'text-alt',
			label: labels.addAnotherEntityAdministrator,
			onClick: () => {
				!userError && !(userList.length > users.length) && addEntityOwnerbuttonClick();
			},
			disabled: userError || userList.length > users.length ? 'disabled' : '',
		};
		return <Button {...props} />;
	}

	const renderDomainBtn = () => {
		const props = {
			svg: IconoirPlusCircle,
			iconSize: 20,
			variant: 'text-alt',
			label: labels.addNewDomain,
			onClick: () => {
				domainValidator() && addDomainbuttonClick(1);
			},
			disabled: (domainError || domainList.length > domains.length) && 'disabled',
		};
		return <Button {...props} />;
	}

	const renderExistingDomainBtn = () => {
		const props = {
			svg: IconoirPlusCircle,
			iconSize: 20,
			variant: 'text-alt',
			label: labels.addExistingDomain,
			onClick: () => {
				domainValidator() && existingDomains.length > 0 && addDomainbuttonClick(2);
			},
			disabled: (domainError || existingDomains.length === 0) && 'disabled',
		};
		return <Button {...props} />;
	}

	const renderDeleteEntityOwnerBtn = (el) => {
		const props = {
			svg: IconoirXmarkCircle,
			iconSize: 20,
			variant: 'text',
			title: labels.remove,
			onClick: () => deleteEntityOwner(el)
		};
		return <Button {...props} />;
	}

	return (
		<Modal
			show={showModal}
			showButtons={true}
			modalsize={'small'}
			modalContainerClass="edit-entity-modal"
			title={labels.editEntity}
			onHide={() => setShowModal(false)}
			closeTitle={labels.closeLabel}
			confirmBtnTitle={labels.save}
			onOkClick={() => setEntityDetails()}
			closeBtnTitle={labels.cancelLabel}
		>
			<StyledModalInnerBody className="customScrollbar">
				<ErrorSummary></ErrorSummary>
				<div className='custom-text-input'>
					<Input
						label={labels.entityName}
						id={selectedNode.id}
						maxLength="100"
						value={entityName?? ''}
						required={true}
						errorMessage={entityNameError ? labels.required : null}
						onChange={(e) => {
							setEntityName(e.target.value);
							setEntityNameError(_.trim(e.target.value).length > 0 ? false : true);
						}}
					/>
				</div>

				<section className="admin-wrap">
					<div class='content'>
						<OutputText>{labels.entityAdministrators}</OutputText>
						<OutputText light>{labels.editEntityAdministratorsInstruction}</OutputText>
					</div>
					<ul>
						{userList.map((el) => {
							return (
								<li key={el.id}>
									<AddProjectOwner
										users={users}
										setUsers={setUsers}
										userLookupType={UserLookupType.NotSpecified}
										key={el.id}
										id={el.id}
										userError={userError}
										setUserError={setUserError}
										hideClearIcon={false}
										setSelectedMember={setSelectedMember}
										className="add-proj-owner-wrap"
									/>
									{
									<span className="remove-icon">
										{renderDeleteEntityOwnerBtn(el)}
									</span>
									}
								</li>
							);
						})}
					</ul>
					{renderAdminBtn()}
				</section>

				<section className="entity-wrap">
					<div class='content'>
						<OutputText>{labels.domains}</OutputText>
						<OutputText light>{labels.editEntityDomainInstruction}</OutputText>
					</div>
					<section className="domain-input-wrapper">
						{domainList.map((el) =>
							el.button == 1 ? (
								<AddNewDomain
									domainList={domainList}
									setEmptyDomain={setEmptyDomain}
									setDomainDuplicate={setDomainDuplicate}
									key={el.id}
									id={el.id}
									remove={removeDomainRow}
									updateDomainName={updateDomainName}
									domainError={domainError}
									isOld={el.isOld}
									domains={domains}
									updateDomainValidation={updateDomainValidation}
								/>
							) : (
								<AddExistingDomain
									domainList={domainList}
									setEmptyDomain={setEmptyDomain}
									setDomainDuplicate={setDomainDuplicate}
									key={el.id}
									id={el.id}
									remove={removeDomainRow}
									updateDomainName={updateExitsingDomainName}
									domainError={domainError}
									existingDomains={existingDomains}
									domains={domains}
								/>
							)
						)}
					</section>
					<section className="add-domain-buttons-container">
						{renderDomainBtn()}
						{renderExistingDomainBtn()}
					</section>
				</section>
			</StyledModalInnerBody>
		</Modal>
	);
};

export default EntityModal;

const StyledModalInnerBody = styled.section`
	// height: calc(100% - 11rem);
	height: 100%;

	.errorMessageWrapper {
		left: 0;
		z-index: 2;
	}

	.custom-text-input:not(.group-input-textbox) {
		border-bottom: solid 0.1rem ${(props) => props.theme.color[`grey250`]};
		.motif-form-field {
			width: var(--px-320);
		}
	}

	.custom-text-input {
		padding: var(--spacing-x-large) 0;
		&.group-input-textbox {
			width: 5%;
		}
	}
	.content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-large);
	}
	.admin-wrap,
	.entity-wrap {
		margin-top: 3rem;
		h4 {
			margin: 0 0 1rem 0;
			padding: 0;
			font-size: 1.4rem;
			font-weight: 600;
			line-height: normal;
		}
		.instructional-text {
			font-size: 1.4rem;
			line-height: 2rem;
		}
	}
	.admin-wrap {
		.instructional-text {
			margin-bottom: 2rem;
		}
		.enter-description {
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
			width: 96%;
			display: inline-block;
		}
		ul {
			padding-left: 0;
		}
	}

	.entity-wrap {
		margin-top: 2rem;
		border-top: solid 0.1rem ${(props) => props.theme.color[`grey250`]};
		padding-top: var(--spacing-medium);
		.add-domain-buttons-container {
			display: flex;
			gap: var(--spacing-large);
		}
		.domain-input-wrapper {
			margin-top: 2rem;
			.add-new-domain-wrap {
				width: 47%;
				margin-bottom: 2rem;
				.motif-typeahead-screen-reader {
					display: none;
				}

				.group-input-textbox {
					width: 100%;
				}
			}
			.add-existing-domain-wrap {
				width: 47%;
				.motif-select-input {
					background: transparent;
					.motif-select-input-arrow {
						top: 1.4rem;
					}
					.motif-select-input-text {
						font-size: 1.4rem;
					}
				}
				.motif-form-field-select-input {
					width: 100%;
					.motif-label {
						top: 1.5rem;
						font-size: 1.2rem;
					}
				}
				.motif-form-field-select-input {
					&.motif-form-field-has-value {
						.motif-label {
							top: 0.5rem;
						}
					}
				}
			}
		}
	}
	.admin-wrap {
		ul {
			li {
				display: flex;
				align-items: baseline;
				margin: 0 0 2rem 0;
				.remove-icon {
					min-height: 5rem;
					display: flex;
					align-items: center;
					svg {
						fill: ${(props) => props.theme.color[`black`]};
					}
				}
			}
		}
		.add-proj-owner-wrap {
			margin: 0;
			.search-box-wrapper {
				width: 22%;
				margin-left: 0;
			}
			.user-selectbox {
				.react-select__input {
					width: 90% !important;
				}
				.react-select__single-value {
					width: 80% !important;
					section {
						text-overflow: ellipsis;
						overflow: hidden;
						white-space: nowrap;
					}
				}
				.clearall {
					margin-top: 0;
					&.disabled {
						visibility: hidden;
					}
				}

				.enterdetail-description {
					margin-left: 0;
				}
			}
		}
	}
`;
